import { type DrawableMixin } from "../objects/drawable_mixin"
import PathObject from "../objects/path"
import Form from "./base"
import Calc from "@/common/util/calculator"

export default class EllipseForm extends Form {
  protected path(width: number, height: number) {
    const commands = this.commands(width, height)

    return new PathObject(commands.join(""), this.defaultParams)
  }

  protected commands(width: number, height: number) {
    const startY = -(height / 2)
    const rx = width / 2
    const ry = height / 2

    const commands = [this.pathMoveTo(0, startY)]

    commands.push(this.pathArcAbs(rx, ry, width / 2, 0))
    commands.push(this.pathArcAbs(rx, ry, 0, height / 2))
    commands.push(this.pathArcAbs(rx, ry, -(width / 2), 0))
    commands.push(this.pathArcAbs(rx, ry, 0, startY))
    commands.push(this.pathClose())

    return commands
  }

  protected cutlineGappedPath(width: number, height: number): DrawableMixin | undefined {
    if (this.isStainlessSteelSign() || this.isEtchedAluminiumSign()) {
      const commands = this.circleCutLine(width, height)

      const path = new PathObject(commands, {
        ...this.defaultParams,
        stroke: "#f08894",
        strokeWidth: this.drawables.cutlineWidth,
        fill: "transparent",
      })

      return path
    }

    return
  }

  protected snappablePaths(width: number, height: number): [DrawableMixin, DrawableMixin] {
    const gap = Calc.mmToPx(3)
    const rx = width / 2
    const ry = height / 2

    if (width > height) {
      const side = [
        this.pathMoveTo(-(gap / 2), height / 2),
        this.pathArcAbs(rx, ry, gap / 2, height / 2, false), // B
        this.pathMoveTo(gap / 2, -(height / 2)),
        this.pathArcAbs(rx, ry, -(gap / 2), -(height / 2), false),
      ]

      const rest = [
        this.pathMoveTo(-(width / 2), 0),
        this.pathArcAbs(rx, ry, -(gap / 2), height / 2, false), // BL
        this.pathMoveTo(gap / 2, height / 2),
        this.pathArcAbs(rx, ry, width / 2, 0, false), // BR
        this.pathArcAbs(rx, ry, gap / 2, -(height / 2), false), // TR
        this.pathMoveTo(-(gap / 2), -(height / 2)),
        this.pathArcAbs(rx, ry, -(width / 2), 0, false), // TL
      ]

      return [new PathObject(side.join(""), this.defaultParams), new PathObject(rest.join(""), this.defaultParams)]
    }

    const side = [
      this.pathMoveTo(-(width / 2), -(gap / 2)),
      this.pathArcAbs(rx, ry, -(width / 2), gap / 2, false), // L
      this.pathMoveTo(width / 2, gap / 2),
      this.pathArcAbs(rx, ry, width / 2, -(gap / 2), false), // R
    ]

    const rest = [
      this.pathMoveTo(-(width / 2), gap / 2),
      this.pathArcAbs(rx, ry, 0, height / 2, false), // BL
      this.pathArcAbs(rx, ry, width / 2, gap / 2, false), // BR
      this.pathMoveTo(width / 2, -(gap / 2)),
      this.pathArcAbs(rx, ry, 0, -(height / 2), false), // TR
      this.pathArcAbs(rx, ry, -(width / 2), -(gap / 2), false), // TL
    ]

    return [new PathObject(side.join(""), this.defaultParams), new PathObject(rest.join(""), this.defaultParams)]
  }

  protected circleCutLine(width: number, height: number): string {
    const rx = width / 2
    const ry = height / 2

    const gap = this.gapLength()
    const legLength = Calc.mmToPx(2.5)
    const segments = 3

    // Step 1: Estimate full circumference
    function ellipseCircumference(rx: number, ry: number): number {
      const h = Math.pow(rx - ry, 2) / Math.pow(rx + ry, 2)

      return Math.PI * (rx + ry) * (1 + (3 * h) / (10 + Math.sqrt(4 - 3 * h)))
    }

    const totalCircumference = ellipseCircumference(rx, ry)
    const gapTotal = segments * gap
    const arcTotal = totalCircumference - gapTotal
    const segmentLength = arcTotal / segments

    // Step 2: Build t -> arc length lookup table
    const arcLookup: { t: number; length: number }[] = []
    let length = 0
    const steps = 1000

    for (let i = 0; i <= steps; i++) {
      const t = (i / steps) * 2 * Math.PI + Math.PI / 2
      const dx = -rx * Math.sin(t)
      const dy = ry * Math.cos(t)
      const dl = Math.sqrt(dx * dx + dy * dy)
      length += dl * ((2 * Math.PI) / steps)

      arcLookup.push({ t, length })
    }

    // Step 3: Find t from arc length
    function getTFromLength(target: number): number {
      for (let i = 1; i < arcLookup.length; i++) {
        const prev = arcLookup[i - 1]
        const curr = arcLookup[i]

        if (curr.length >= target) {
          const ratio = (target - prev.length) / (curr.length - prev.length)

          return prev.t + ratio * (curr.t - prev.t)
        }
      }

      return 2 * Math.PI
    }

    const commands: string[] = []

    // Step 4: Build each arc segment
    let currentLength = 0

    for (let i = 0; i < segments; i++) {
      const tStart = getTFromLength(currentLength)
      const tEnd = getTFromLength(currentLength + segmentLength)

      const legStartX = (rx + legLength) * Math.cos(tStart)
      const legStartY = (ry + legLength) * Math.sin(tStart)

      const arcStartX = rx * Math.cos(tStart)
      const arcStartY = ry * Math.sin(tStart)
      const arcEndX = rx * Math.cos(tEnd)
      const arcEndY = ry * Math.sin(tEnd)

      // leg + arc
      commands.push(this.pathMoveTo(legStartX, legStartY))
      commands.push(this.pathLineToAbs(arcStartX, arcStartY))
      commands.push(this.pathArcAbs(rx, ry, arcEndX, arcEndY, true))

      currentLength += segmentLength + gap
    }

    return commands.join("")
  }
}
